import { setAuthTokenGetter, setBaseUrl } from "@workspace/api-client-react";

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, "");
}

/**
 * The Clerk session token for the active session, read from the clerk-js global
 * (populated once Clerk loads). Used as a bearer for cross-origin API calls
 * (desktop loopback / VITE_API_URL) where the session cookie is a blocked
 * third-party cookie; the api-server accepts either.
 */
function clerkSessionToken(): Promise<string | null> | null {
  const clerk = (
    window as unknown as {
      Clerk?: { session?: { getToken: () => Promise<string | null> } };
    }
  ).Clerk;
  return clerk?.session?.getToken() ?? null;
}

export function isLocalDevHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]"
  );
}

/**
 * The desktop shell serves the bundled player from its own origin
 * (omnimedia://) and injects the configured server origin via the preload
 * bridge. Everything API-shaped must go there instead of same-origin.
 */
function desktopServerUrl(): string | null {
  if (typeof window === "undefined") return null;
  const url = window.omnimediaDesktop?.serverUrl?.trim();
  return url ? stripTrailingSlash(url) : null;
}

/**
 * The remote API origin the player talks to when it is NOT same-origin: the
 * desktop shell's configured server, else `VITE_API_URL` (local Mac → remote
 * API). Null on the plain same-origin web build. Exported so Clerk can point
 * its proxy at the SAME origin whose api-server issued the session token (see
 * `resolveClerkProxyUrl` in App.tsx).
 */
export function remoteApiOrigin(): string | null {
  const desktop = desktopServerUrl();
  if (desktop) return desktop;
  const explicit = import.meta.env.VITE_API_URL?.trim();
  return explicit ? stripTrailingSlash(explicit) : null;
}

/**
 * Point the generated client at a remote API origin when one applies:
 * the desktop shell's configured server, or `VITE_API_URL` (local Mac →
 * remote API). On the plain web build, leave it unset so `/api` stays
 * same-origin.
 *
 * IMPORTANT: only set `VITE_API_URL` in `artifacts/web/.env.local`
 * (gitignored, per-machine). Never put it in the committed `.env` — Vite
 * bakes `.env` into production builds, which would point the published site
 * at the dev server and break auth (cross-origin requests drop the Clerk
 * session cookie → 401).
 */
export function configureWebApiClient(): void {
  const desktop = desktopServerUrl();
  if (desktop) {
    setBaseUrl(desktop);
    // Cross-origin to the API (loopback app origin ≠ server origin): the Clerk
    // session cookie is a blocked third-party cookie, so send the session token
    // as a bearer instead.
    setAuthTokenGetter(clerkSessionToken);
    return;
  }
  const explicit = import.meta.env.VITE_API_URL?.trim();
  if (explicit) {
    setBaseUrl(stripTrailingSlash(explicit));
    setAuthTokenGetter(clerkSessionToken);
  }
}

/**
 * Absolute URL for an UNAUTHENTICATED API endpoint called with plain `fetch`.
 *
 * The generated client resolves this internally, but the public marketing
 * pages must not import it — that would pull Clerk and the query client into
 * the landing bundle. Resolves the same base `configureWebApiClient` uses:
 * the desktop shell's server, else `VITE_API_URL`, else same-origin `/api`.
 *
 * @param path endpoint path below `/api`, with a leading slash.
 */
export function apiUrl(path: string): string {
  const base = desktopServerUrl() ?? import.meta.env.VITE_API_URL?.trim() ?? "";
  return `${base ? stripTrailingSlash(base) : ""}/api${path}`;
}

export function usesRemoteApiOnLocalhost(): boolean {
  const explicit = import.meta.env.VITE_API_URL?.trim();
  return Boolean(explicit && isLocalDevHost(window.location.hostname));
}

/**
 * ws(s) origin for `/api/realtime` when the API is remote (desktop shell or
 * `VITE_API_URL`), or null to use the page's own host.
 */
export function getApiWebSocketOrigin(): string | null {
  const remote = desktopServerUrl() ?? import.meta.env.VITE_API_URL?.trim();
  if (!remote) return null;
  return stripTrailingSlash(remote)
    .replace(/^https:/i, "wss:")
    .replace(/^http:/i, "ws:");
}
